/**
 * i18n bootstrap (react-i18next).
 *
 * Languages: English + Portuguese, bundled (small) so there's no async load.
 * The active language is driven by the selected business's `language` field
 * (see <LanguageSync/>), persisted to localStorage so reloads keep it until the
 * business data arrives.
 *
 * Usage in components: const { t } = useTranslation();  t('common.save')
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import pt from './locales/pt.json';

export const SUPPORTED_LANGS = ['pt', 'en'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Map the business `language` field (e.g. "Portuguese (Brazil)") to a lng code. */
export function toLang(businessLanguage?: string | null): Lang {
    const v = (businessLanguage ?? '').toLowerCase();
    if (v.startsWith('en') || v.includes('english')) return 'en';
    if (v.startsWith('pt') || v.includes('portug')) return 'pt';
    return 'pt';
}

const stored = (typeof localStorage !== 'undefined' && localStorage.getItem('gs-lang')) || '';
/** Before a business language is known (e.g. onboarding), fall back to the
 *  browser's language so an English-speaking owner isn't stuck in Portuguese.
 *  A stored choice (or the business's language via <LanguageSync/>) always wins. */
function detectInitialLng(): Lang {
    if ((SUPPORTED_LANGS as readonly string[]).includes(stored)) return stored as Lang;
    const nav = (typeof navigator !== 'undefined' && navigator.language) || '';
    return nav.toLowerCase().startsWith('en') ? 'en' : 'pt';
}
const initialLng: Lang = detectInitialLng();

i18n.use(initReactI18next).init({
    resources: {
        en: { translation: en },
        pt: { translation: pt },
    },
    lng: initialLng,
    fallbackLng: 'en',
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
});

export default i18n;
